import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, KeyRound, Loader2, CreditCard, Coins, Check, Sparkles } from "lucide-react";
import { billingApi } from "@/api/billing";
import { vaultSecurityApi } from "@/api/vaultSecurity";

/* ==========================================================================
   KEY / PAYMENT GATEWAY — site-wide generation intercept.
   Lane 1  BYOK key → vaultSecurityApi.byokRegSet → unlock + fire pending gen.
   Lane 2  three credit tiers ($10/110 · $25/300 POPULAR · $50/650).
   Lane 3  inline gateway drawer → Stripe (works now) · PayPal (auto-disables
           when unconfigured). On paid → unlock + fire pending gen.
   ========================================================================== */
const GOLD = "#FFE08A";
const TIERS = [
  { key: "coins_50", fallbackAud: 5, credits: "50 Credits", popular: false },
  { key: "coins_110", fallbackAud: 10, credits: "110 Credits", popular: true },
  { key: "coins_240", fallbackAud: 20, credits: "240 Credits", popular: false },
];

// Format a minor-unit amount in the TRUE charge currency, using the visitor's
// own locale (so an AUD price reads "$5" in AU and "A$5" elsewhere). No FX
// conversion — we never show a price that differs from what Stripe charges.
const fmtMoney = (amountMinor, currency) => {
  try {
    return new Intl.NumberFormat(navigator.language || "en", {
      style: "currency", currency: (currency || "AUD").toUpperCase(),
      maximumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
    }).format(amountMinor / 100);
  } catch { return `$${(amountMinor / 100).toFixed(2)}`; }
};

export default function KeyPaymentGateway({ open, onClose, onUnlocked }) {
  const [keyVal, setKeyVal] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [tier, setTier] = useState(null);              // selected lookup_key
  const [payBusy, setPayBusy] = useState(false);
  const [paypal, setPaypal] = useState({ enabled: false, checked: false });
  const [priceMap, setPriceMap] = useState({});

  useEffect(() => {
    if (!open) { setTier(null); setKeyVal(""); return; }
    billingApi.paypalConfig()
      .then((c) => setPaypal({ enabled: !!c.enabled, checked: true }))
      .catch(() => setPaypal({ enabled: false, checked: true }));
    // Pull the REAL Stripe amount + currency per pack so the display always
    // matches the actual charge (localised via Intl, no hardcoded currency).
    billingApi.plans()
      .then((d) => {
        const m = {};
        (d.plans || []).forEach((p) => {
          if (p.amount != null) m[p.lookup_key] = { amount: p.amount, currency: p.currency };
        });
        setPriceMap(m);
      })
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  const priceFor = (t) => {
    if (!t) return "";
    const p = priceMap[t.key];
    return p ? fmtMoney(p.amount, p.currency) : fmtMoney((t.fallbackAud || 0) * 100, "AUD");
  };

  const saveKey = async () => {
    const v = keyVal.trim();
    if (v.length < 8) { toast.error("Enter a valid key"); return; }
    setSavingKey(true);
    try {
      await vaultSecurityApi.byokRegSet("FAL_KEY", v);
      toast.success("Key saved — resuming your render ✓");
      onUnlocked?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save key"); }
    finally { setSavingKey(false); }
  };

  const payStripe = async () => {
    if (!tier) return;
    setPayBusy(true);
    try {
      const { checkout_url, session_id } = await billingApi.checkout({ lookup_key: tier, origin_url: window.location.origin });
      const win = window.open(checkout_url, "_blank");
      if (!win) { window.location.href = checkout_url; return; }
      // poll until Stripe marks it paid, then unlock + fire pending gen
      const start = Date.now();
      const timer = setInterval(async () => {
        if (Date.now() - start > 300000) { clearInterval(timer); setPayBusy(false); return; }
        try {
          const s = await billingApi.checkoutStatus(session_id);
          if (s.payment_status === "paid" || s.status === "complete") {
            clearInterval(timer); setPayBusy(false);
            toast.success("Payment complete — resuming your render ✓");
            onUnlocked?.(); onClose?.();
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Stripe checkout failed"); setPayBusy(false); }
  };

  const payPaypal = async () => {
    if (!tier || !paypal.enabled) return;
    setPayBusy(true);
    try {
      const { order_id, approve_url } = await billingApi.paypalCreateOrder(tier);
      if (approve_url) window.open(approve_url, "_blank");
      const start = Date.now();
      const timer = setInterval(async () => {
        if (Date.now() - start > 300000) { clearInterval(timer); setPayBusy(false); return; }
        try {
          const cap = await billingApi.paypalCaptureOrder(order_id);
          if (cap?.ok || cap?.status === "COMPLETED") {
            clearInterval(timer); setPayBusy(false);
            toast.success("Payment complete — resuming your render ✓");
            onUnlocked?.(); onClose?.();
          }
        } catch { /* buyer hasn't approved yet — keep polling */ }
      }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "PayPal unavailable"); setPayBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" data-testid="key-payment-gateway">
      <div onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full sm:max-w-md max-h-[92vh] overflow-y-auto no-scrollbar bg-[#0e0e0e] border-2 border-[#FFE08A] sm:border-black shadow-[0_-8px_40px_rgba(0,0,0,0.6)] sm:shadow-[8px_8px_0px_#000] m-0 sm:m-4">
        {/* header */}
        <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 bg-[#0e0e0e] border-b-2 border-[#1a1a1a]">
          <Sparkles size={16} style={{ color: GOLD }} />
          <h3 className="flex-1 font-serif text-xl text-white leading-none">Unlock generation</h3>
          <button onClick={onClose} data-testid="gateway-close" className="w-8 h-8 grid place-items-center border-2 border-black bg-[#141414] shadow-[2px_2px_0px_#000]"><X size={14} className="text-white" /></button>
        </div>

        <div className="p-4 space-y-5">
          {/* LANE 1 — BYOK */}
          <div data-testid="gateway-lane-byok">
            <div className="flex items-center gap-2 mb-2"><KeyRound size={13} style={{ color: GOLD }} /><p className="font-mono text-[10px] font-black uppercase tracking-widest text-white">Bring your own key</p></div>
            <input type="password" value={keyVal} onChange={(e) => setKeyVal(e.target.value)} data-testid="gateway-byok-input"
              placeholder="Paste your Fal.ai key"
              className="w-full border-2 border-black bg-[#0e0e0e] text-cream font-mono text-[11px] px-3 py-2.5 placeholder:text-white/25 focus:border-[#FFE08A] outline-none" />
            <button onClick={saveKey} disabled={savingKey} data-testid="gateway-byok-save"
              className="mt-2 w-full flex items-center justify-center gap-1.5 border-2 border-black bg-[#C9B6FF] text-black font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2.5 shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
              {savingKey ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save key & continue
            </button>
          </div>

          <div className="flex items-center gap-3"><div className="flex-1 h-px bg-white/10" /><span className="font-mono text-[8px] uppercase tracking-widest text-white/35">or buy credits</span><div className="flex-1 h-px bg-white/10" /></div>

          {/* LANE 2 — credit tiers */}
          <div data-testid="gateway-lane-tiers">
            <div className="flex items-center gap-2 mb-2"><Coins size={13} style={{ color: GOLD }} /><p className="font-mono text-[10px] font-black uppercase tracking-widest text-white">Credit packs</p></div>
            <div className="grid grid-cols-3 gap-2">
              {TIERS.map((t) => (
                <button key={t.key} onClick={() => setTier(t.key)} data-testid={`gateway-tier-${t.key}`}
                  className={`relative text-center p-3 border-2 transition-[transform,box-shadow] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${
                    tier === t.key ? "border-[#FFE08A] bg-[#18181B] shadow-[3px_3px_0px_#000]"
                    : t.popular ? "border-[#FFE08A]/70 bg-[#141414] shadow-[3px_3px_0px_#000]" : "border-black bg-[#141414] shadow-[3px_3px_0px_#000]"}`}>
                  {t.popular && <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#FFE08A] text-black font-mono text-[7px] font-black uppercase px-1.5 py-0.5 border border-black">🔥 Popular</span>}
                  <p className="font-serif text-xl text-white leading-none mt-1">{priceFor(t)}</p>
                  <p className="mt-1 font-mono text-[8px] uppercase tracking-widest text-white/50">{t.credits}</p>
                </button>
              ))}
            </div>
          </div>

          {/* LANE 3 — inline gateway drawer */}
          {tier && (
            <div data-testid="gateway-pay-drawer" className="border-2 border-[#FFE08A]/50 bg-[#101014] p-3 space-y-2">
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/60">Pay for {priceFor(TIERS.find((x) => x.key === tier))} · {TIERS.find((x) => x.key === tier)?.credits}</p>
              <button onClick={payStripe} disabled={payBusy} data-testid="gateway-pay-stripe"
                className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2.5 shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
                {payBusy ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />} Pay with Stripe
              </button>
              <button onClick={payPaypal} disabled={!paypal.enabled || payBusy} data-testid="gateway-pay-paypal"
                className={`w-full flex items-center justify-center gap-2 border-2 border-black font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2.5 ${paypal.enabled ? "bg-[#003087] text-white shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none" : "bg-[#1a1a1a] text-white/35 cursor-not-allowed"}`}>
                Pay with PayPal
              </button>
              {paypal.checked && !paypal.enabled && (
                <p data-testid="gateway-paypal-note" className="font-mono text-[8px] text-white/45 leading-relaxed">PayPal not configured yet. Please use Stripe or contact support.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
